import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import type { Product } from '@/api/products';
import { useDirectSaleStore } from '@/stores/directSaleStore';
import { DirectSaleCatalog } from '../DirectSaleCatalog';

/**
 * The desktop's way to another product: the catalogue as an overlay over
 * the editor, so that no path leads back to the catalogue page (R9).
 *
 * Loading, the failed load with its retry and the search without a match
 * are the catalogue's own — the overlay only frames it and guards the
 * switch: a product change discards the design, so a customer who has one
 * confirms first (R10).
 */
export function DirectSaleDesktopCatalog({
  open,
  onOpenChange,
  product,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The product in the editor; its card carries the "Aktuell" badge. */
  product: Product;
}) {
  const { t } = useTranslation();
  // The product the customer picked while a design exists.
  const [pendingSwitch, setPendingSwitch] = useState<Product | null>(null);
  const [count, setCount] = useState<number | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // The catalogue unmounts with the overlay and starts unfiltered the next
  // time; a count or a question left over from the last visit would be
  // about something the customer no longer sees.
  useEffect(() => {
    if (open) return;
    setCount(null);
    setPendingSwitch(null);
  }, [open]);

  // Stable, because the catalogue reports from an effect that depends on it.
  const handleCount = useCallback((next: number) => setCount(next), []);

  const switchTo = (picked: Product) => {
    useDirectSaleStore.getState().selectProduct(picked.id);
    setPendingSwitch(null);
    onOpenChange(false);
  };

  const handleSelect = (picked: Product) => {
    if (picked.id === product.id) {
      onOpenChange(false);
      return;
    }
    // Read at the moment of the click: the overlay does not need to
    // re-render with every change of the design behind it.
    const hasDesign =
      useDirectSaleStore.getState().decoratedPositions.length > 0;
    if (hasDesign) {
      setPendingSwitch(picked);
      return;
    }
    switchTo(picked);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          ref={contentRef}
          closeLabel={t('directSale.design.close')}
          // The catalogue explains itself; Radix would warn about the
          // missing description otherwise.
          aria-describedby={undefined}
          className="h-[calc(100dvh-48px)] w-[calc(100vw-48px)] max-w-[1400px] gap-0 p-0"
          onOpenAutoFocus={(event) => {
            // Radix would focus the first focusable element, which is the
            // search field today — asking for it by name keeps that true
            // when the catalogue grows something above it.
            const search = contentRef.current?.querySelector<HTMLInputElement>(
              'input[type="search"]'
            );
            if (!search) return;
            event.preventDefault();
            search.focus();
          }}
        >
          <DialogTitle className="flex-none px-6 pb-4 pt-6">
            {/* One text, not a title and a badge: the dialog takes its name
                from here, and a screen reader reads it as one line. */}
            {count === null
              ? t('directSale.mobile.chooseProduct')
              : `${t('directSale.mobile.chooseProduct')} (${count})`}
          </DialogTitle>
          {/* The grid scrolls, the title stays: at 1024 × 768 the overlay is
              shorter than two rows of cards. */}
          <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">
            <DirectSaleCatalog
              selectedProductId={product.id}
              selectedLabel={t('directSale.mobile.current')}
              onSelect={handleSelect}
              onCount={handleCount}
            />
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={pendingSwitch !== null}
        // "Behalten", Escape and a click beside it all mean the same: the
        // question goes away, the overlay stays for another choice.
        onOpenChange={(next) => {
          if (!next) setPendingSwitch(null);
        }}
        title={t('directSale.mobile.switchTitle', {
          name: pendingSwitch?.name ?? '',
        })}
        description={t('directSale.mobile.switchBody')}
        cancelLabel={t('directSale.mobile.keep')}
        confirmLabel={t('directSale.mobile.switchConfirm')}
        onConfirm={() => {
          if (pendingSwitch) switchTo(pendingSwitch);
        }}
      />
    </>
  );
}
